import {
  COOKIE_SERVICE,
  type CookieService,
  LOGGER,
  type Logger,
} from "@packages/common";
import { CONFIG, type Config } from "@packages/config";
import { Inject, Injectable } from "@webmotors/ioc";
import {
  COOKIE_CONSENTS_SERVICE,
  type CookieConsentsService,
} from "../cookie-consents";
import { LastSearchesEntryDTO } from "./dtos";
import {
  type Filters,
  LAST_SEARCHES_SERVICE,
  type ParsedLastSearch,
} from "./last-searches.types";

type LastSearchEntry = {
  route: string;
  query: string;
};

@Injectable(LAST_SEARCHES_SERVICE)
export class LastSearchesService {
  private readonly COOKIE_NAME: string;
  private readonly DAYS_TO_EXPIRE = 90;
  private readonly MAX_COOKIE_LENGTH = 4000;
  private readonly MAX_SEARCH_HISTORY = 10;
  private readonly logger: Logger;

  constructor(
    @Inject(CONFIG) private readonly config: Config,
    @Inject(LOGGER) private readonly loggerFactory: Logger,
    @Inject(COOKIE_SERVICE) private readonly cookieService: CookieService,
    @Inject(COOKIE_CONSENTS_SERVICE)
    private readonly cookieConsentsService: CookieConsentsService
  ) {
    this.logger = this.loggerFactory.create(LAST_SEARCHES_SERVICE);
    this.COOKIE_NAME = this.config.lastSearchesCookieName || "";
  }

  /**
   * Parse last searches cookie value from raw string.
   * Useful for SSR where cookieService is not available.
   *
   * @param cookieValue - Raw cookie value as string.
   * @returns Array of parsed last searches.
   */
  parseLastSearchesCookie(cookieValue: string | null): ParsedLastSearch[] {
    try {
      if (!cookieValue) return [];

      const searches: LastSearchEntry[] = JSON.parse(
        decodeURIComponent(cookieValue)
      );
      if (!Array.isArray(searches)) return [];

      return searches.map((search) => {
        const params = new URLSearchParams(search.query);
        const brand = params.get("marca1") || undefined;
        const model = params.get("modelo1") || undefined;
        const location = params.get("estadocidade") || undefined;

        let vehicleType: "car" | "bike" | undefined;
        if (search.route.includes("motos")) {
          vehicleType = "bike";
        } else if (search.route.includes("carros")) {
          vehicleType = "car";
        }

        return {
          brand,
          model,
          vehicleType,
          location,
          route: search.route,
          query: search.query,
          fullUrl: `${search.route}${search.query}`,
        };
      });
    } catch (error) {
      console.error("[parseLastSearchesCookie] Error:", error);
      return [];
    }
  }

  clearAll() {
    this.logger.log("[clearAll] clearing search cookies: ", this.COOKIE_NAME);

    this.cookieService.removeCookie(this.COOKIE_NAME);
  }

  /**
   * Save a new search entry into the cookie if the cookie category is active and filters are valid.
   *
   * @param filters - Filters used for the search.
   * @param url - URL of the search.
   */
  async save({
    filters,
    url,
  }: {
    filters: Filters;
    url: string;
  }): Promise<void> {
    try {
      if (!this.COOKIE_NAME) {
        return;
      }

      if (!(await this.cookieConsentsService.isSearchEnabled())) {
        return;
      }

      if (!this.isValidFilters(filters)) {
        return;
      }

      const lastSearches = this.get();
      const [route = "", query = ""] = url.split("?");

      if (!route || !query) {
        return;
      }

      const newSearch = new LastSearchesEntryDTO({
        route,
        query: `?${query}`,
      });

      const clearedSearches = this.removeIfAlreadySearchExists(
        lastSearches,
        newSearch
      );

      const updatedSearches = this.ensureCookieSizeLimit([
        newSearch,
        ...clearedSearches,
      ]);

      this.cookieService.save(this.COOKIE_NAME, updatedSearches, {
        expires: this.getExpirationDate(),
      });
    } catch (error) {
      this.logger.error("[Set] Failed to set lastSearchesCookie.", error);
      return;
    }
  }

  /**
   * Retrieve the search history from the cookie.
   *
   * @returns The list of saved searches.
   */
  get(): LastSearchesEntryDTO[] {
    try {
      if (!this.COOKIE_NAME) {
        return [];
      }
      const searches =
        this.cookieService.get<LastSearchesEntryDTO[]>(this.COOKIE_NAME) ?? [];
      return Array.isArray(searches) ? searches : [];
    } catch (error) {
      this.logger.error("[Get] Failed to get lastSearchesCookie.", error);
      return [];
    }
  }

  /**
   * Retrieve and parse the search history from the cookie.
   *
   * @returns Array of parsed last searches with extracted information.
   */
  getParsed(): ParsedLastSearch[] {
    try {
      const searches = this.get();

      const cookieValue = JSON.stringify(searches);
      return this.parseLastSearchesCookie(cookieValue);
    } catch (error) {
      this.logger.error(
        "[GetParsed] Failed to parse lastSearchesCookie.",
        error
      );
      return [];
    }
  }

  /**
   * Validate if the filters are suitable for saving the search.
   *
   * @param filters - Filters to validate.
   * @returns True if valid, otherwise false.
   */
  private isValidFilters(filters: Filters): boolean {
    const hasMake = filters.makes?.[0]?.[0]?.name === "marca";
    const hasModel = filters.makes?.[0]?.[1]?.name === "modelo";
    return hasMake && hasModel && !!filters.vehicleType;
  }

  /**
   * Check if a search entry already exists in the history.
   * If it exists, remove it.
   *
   * @param searches - Existing search history.
   * @param newSearch - The new search entry to check and update.
   * @returns The updated array of searches.
   */
  private removeIfAlreadySearchExists(
    searches: LastSearchesEntryDTO[],
    newSearch: LastSearchesEntryDTO
  ): LastSearchesEntryDTO[] {
    try {
      const newSearchParams = new URLSearchParams(newSearch.query);
      newSearchParams.sort();
      const newSearchUrl = `${newSearch.route}?${newSearchParams.toString()}`;

      const updatedSearches = searches.filter((search) => {
        const searchParams = new URLSearchParams(search.query);
        searchParams.sort();
        const searchUrl = `${search.route}?${searchParams.toString()}`;
        return searchUrl.toLowerCase() !== newSearchUrl.toLowerCase();
      });

      return updatedSearches;
    } catch {
      return searches;
    }
  }

  /**
   * Ensure the cookie does not exceed the size or history length limits.
   *
   * @param searches - List of searches to validate.
   * @returns The trimmed list of searches.
   */
  private ensureCookieSizeLimit(
    searches: LastSearchesEntryDTO[]
  ): LastSearchesEntryDTO[] {
    const maxEntries = this.MAX_SEARCH_HISTORY;

    for (let length = maxEntries; length > 0; length--) {
      const serializedSearches = JSON.stringify(searches.slice(0, length));
      if (serializedSearches.length <= this.MAX_COOKIE_LENGTH) {
        return searches.slice(0, length);
      }
    }

    return [];
  }

  /**
   * Generate the expiration date for the cookie.
   *
   * @returns The expiration date.
   */
  private getExpirationDate(): Date {
    return new Date(Date.now() + this.DAYS_TO_EXPIRE * 24 * 60 * 60 * 1000);
  }
}
